import { featureCollection, distance, point } from "@turf/turf"
import GeoJSON from "geojson"
import Geohash from "latlon-geohash"
import { forEach } from "lodash-es"
import type { GridHeatVO, RuralSceneListVO } from "@/api"
import { accMul } from "@/util/util"
import { ruralHeatRadius } from "@/constants/constant"
import { getRectangPolygon, getCirclePolygon, isRectangleInCircle } from "./graphic-calc"
// ===========================================
const colors = [
  [125, 207, 250, 150],
  [255, 248, 0, 180],
  [255, 80, 0, 200],
]
// ==============================================
// 过滤热力数据
export function getFilterHeatData1(heats: GridHeatVO[], ruralList: RuralSceneListVO[]) {
  const cloneHeats = heats.slice() || []
  const listMap = new Map()
  for (let i = 0; i < ruralList.length; i++) {
    const ruralItem = ruralList[i]
    const circle = getCirclePolygon([ruralItem.lon, ruralItem.lat], accMul(ruralHeatRadius, 0.01))
    for (let j = 0; j < cloneHeats.length; j++) {
      const heat = cloneHeats[j]
      const rectange = getRectangPolygon(heat.gridId)
      if (isRectangleInCircle(circle, rectange)) {
        listMap.set(heat.gridId, heat)
        cloneHeats.splice(j, 1)
        j--
      }
    }
  }
  return Array.from(listMap.values())
}

// 过滤热力数据
export function getFilterHeatData2(heats: GridHeatVO[], ruralList: RuralSceneListVO[]) {
  const result: GridHeatVO[] = []
  const copyHeats = heats.slice()
  forEach(ruralList, (rural) => {
    const { lon, lat } = rural
    let len = copyHeats.length
    while (len > 0) {
      const g = copyHeats[len - 1]
      const { latitude, longitude } = g
      const dis = distance(point([lon, lat]), point([longitude, latitude]))
      if (dis < 3.0015) {
        result.push(g)
        copyHeats.splice(len, 1)
        len--
      }
      len--
    }
  })
  return result
}

export function getFilterHeatData3(grid: GridHeatVO[], nc: RuralSceneListVO[]) {
  const res = []
  const copyGrid = grid.slice()
  const copyNc = nc.slice()
  let ncLen = nc.length
  while (ncLen > 0) {
    const n = copyNc[ncLen - 1]
    const { lon, lat } = n
    let len = copyGrid.length
    while (len > 0) {
      const g = copyGrid[len - 1]
      const { latitude, longitude } = g
      const dis = distance(point([lon, lat]), point([longitude, latitude]))
      if (dis <= 3.0015) {
        res.push(g)
        copyGrid.splice(len - 1, 1)
        len--
      }
      len--
    }
    ncLen--
  }
  return res
}

// 获取热力集合
export function getHeatCollection(grids: GridHeatVO[]) {
  const group = []
  grids?.sort((a, b) => {
    return b.crowdCount - a.crowdCount
  })
  // 最大值取前1/100的那一位，用于异常数据处理
  const maxIdx = Math.floor(grids.length * 0.005)
  const maxCountAd = grids[maxIdx] ? grids[maxIdx]?.crowdCount : 100
  for (const item of grids) {
    const [r, g, b, a] = getHeatColor(maxCountAd, item.crowdCount, colors) || [255, 255, 255, 0]
    const rectang = getRectangPolygon(item.gridId, {
      count: item.crowdCount,
      latitude: item.latitude,
      longitude: item.longitude,
      // 除以2是为了降低不透明度
      color: `rgba(${r},${g},${b},${(a / 255 / 2)?.toFixed(1) || 0})`,
    })
    group.push(rectang)
  }
  return featureCollection(group)
}

/**
 * 通过hash获取热力collection
 * @param grids 数据
 * @param compareType 热力比较类型：1.oneself:当前数据grids自行比较 2.fixed:每个grid与固定最大值进行比较
 * @param maxCount 最大值或默认最大值
 * @param isAbnormal 是否处理异常数据
 * @param field 取值字段
 */
export function getHeatCollectionWithHash(
  grids: GridHeatVO[],
  compareType = "oneself",
  maxCount = 100,
  isAbnormal = true,
  field = "crowdCount",
) {
  const group = []
  // 从大到小排序
  grids?.sort((a, b) => {
    return b[field] - a[field]
  })
  let maxCountAd
  if (compareType === "oneself") {
    // 最大值取前1/100的那一位，用于异常数据处理
    const maxIdx = isAbnormal ? Math.floor(grids.length * 0.005) : 0
    maxCountAd = grids[maxIdx] ? grids[maxIdx]?.[field] : maxCount
  } else {
    maxCountAd = maxCount
  }
  for (const item of grids) {
    const [r, g, b, a] = getHeatColor(maxCountAd, item[field], colors) || [255, 255, 255, 0]

    group.push({
      ...item,
      // 除以2是为了降低不透明度
      color: [r, g, b, Math.floor(a / 2)],
    })
  }
  return group
}

// 获取热力图颜色
function getHeatColor(max: number, value: number, colorRange: number[][]) {
  if (value >= max) {
    // eslint-disable-next-line no-param-reassign
    value = max
  }
  const len = colorRange.length
  const ratio = value / max
  const idx = Math.floor(ratio * (len - 1))

  const start = colorRange[idx]
  if (!start) {
    return colorRange[len - 1]
  }
  const end = colorRange[idx + 1]
  if (!end) {
    return start.slice(0)
  }
  const step = max / (len - 1)
  const r = Math.max((value - idx * step) / step, 0)
  const res = []
  for (let i = 0; i < 4; i++) {
    let v = start[i] + Math.round((end[i] - start[i]) * r)
    if (v > 255) {
      v = 255
    } else if (v < 0) {
      v = 0
    }
    res[i] = v
  }
  return res
}

// 获取Geojson
export function getGeojson(grids: GridHeatVO[]) {
  grids?.sort((a, b) => {
    return b.crowdCount - a.crowdCount
  })
  // 最大值取前1/100的那一位，用于异常数据处理
  const maxIdx = Math.floor(grids.length * 0.005)
  const maxCountAd = grids[maxIdx] ? grids[maxIdx]?.crowdCount : 100
  const resolveGrids = grids.map((grid: GridHeatVO) => {
    const { gridId, crowdCount, latitude, longitude } = grid
    // 西南, 东北点坐标
    const { sw, ne } = Geohash.bounds(gridId)
    // const [minlat, minlon, maxlat, maxlon] = NGeohash.decode_bbox(gridId)
    // 获取热力图颜色
    const [r, g, b, a] = getHeatColor(maxCountAd, crowdCount, colors) || [255, 255, 255, 0]
    return {
      color: `rgba(${r},${g},${b},${(a / 255)?.toFixed(1) || 0})`,
      count: crowdCount,
      latitude,
      longitude,
      polygon: [
        [
          [sw.lon, sw.lat],
          [sw.lon, ne.lat],
          [ne.lon, ne.lat],
          [ne.lon, sw.lat],
          [sw.lon, sw.lat],
        ],
      ],
    }
  })
  return (GeoJSON as any).parse(resolveGrids, { Polygon: "polygon" })
}
